import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CancelWorkOrderDto,
  CreateWorkOrderDto,
  UpdateWorkOrderDto,
  WorkOrder,
  WorkOrderStatus,
} from '../models/work-order.model';
import { OrganizationContextService } from './organization-context.service';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Work Order Service
 *
 * HTTP wrapper over `/work-orders`. Holds a signal-backed cache of the
 * active org's WOs so the list view stays reactive across creates /
 * updates / WS-driven refreshes.
 *
 * Mirrors the customer/drone service patterns in this app — `loadAll()`
 * fires on org-context change, `fetchOne()` is a one-off for the detail
 * screen, and the cache is patched optimistically on every mutation so
 * the list updates without a full reload.
 *
 * Reference: agent-os/specs/2026-04-30-marketplace-work-orders/spec.md
 */
@Injectable({ providedIn: 'root' })
export class WorkOrderService {
  private http = inject(HttpClient);
  private organizationContext = inject(OrganizationContextService);
  private apiUrl = environment.apiUrl;

  private workOrdersSignal = signal<WorkOrder[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly workOrders = computed(() => this.workOrdersSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        void this.loadAll();
      } else {
        this.workOrdersSignal.set([]);
      }
    });
  }

  /**
   * Load the org's work orders. Optional filters mirror the Hub's
   * query params. Returns nothing — readers consume `workOrders()`.
   */
  async loadAll(filters?: {
    status?: WorkOrderStatus;
    assignedPilotProfileId?: string;
    jobId?: string;
    fieldId?: string;
    page?: number;
    limit?: number;
  }): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) return;
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      let params = new HttpParams()
        .set('page', String(filters?.page ?? 1))
        .set('limit', String(filters?.limit ?? 100));
      if (filters?.status) params = params.set('status', filters.status);
      if (filters?.assignedPilotProfileId) {
        params = params.set('assignedPilotProfileId', filters.assignedPilotProfileId);
      }
      if (filters?.jobId) params = params.set('jobId', filters.jobId);
      if (filters?.fieldId) params = params.set('fieldId', filters.fieldId);

      const response = await firstValueFrom(
        this.http.get<ApiResponse<WorkOrder[]>>(`${this.apiUrl}/work-orders`, { params }),
      );
      if (response.success && response.data) {
        this.workOrdersSignal.set(response.data);
      } else {
        throw new Error(response.error || 'Failed to load work orders');
      }
    } catch (error: any) {
      console.error('Error loading work orders:', error);
      this.errorSignal.set(error.message || 'Failed to load work orders');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Single WO read; the detail screen's primary entry point. */
  async fetchOne(id: string): Promise<WorkOrder | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<WorkOrder>>(`${this.apiUrl}/work-orders/${id}`),
      );
      if (response.success && response.data) {
        this.upsertCache(response.data);
        return response.data;
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching work order:', error);
      return null;
    }
  }

  async create(dto: CreateWorkOrderDto): Promise<WorkOrder | null> {
    this.errorSignal.set(null);
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<WorkOrder>>(`${this.apiUrl}/work-orders`, dto),
      );
      if (response.success && response.data) {
        this.upsertCache(response.data);
        return response.data;
      }
      throw new Error(response.error || 'Failed to create work order');
    } catch (error: any) {
      console.error('Error creating work order:', error);
      this.errorSignal.set(error.message || 'Failed to create work order');
      return null;
    }
  }

  async update(id: string, dto: UpdateWorkOrderDto): Promise<WorkOrder | null> {
    this.errorSignal.set(null);
    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<WorkOrder>>(`${this.apiUrl}/work-orders/${id}`, dto),
      );
      if (response.success && response.data) {
        this.upsertCache(response.data);
        return response.data;
      }
      throw new Error(response.error || 'Failed to update work order');
    } catch (error: any) {
      console.error('Error updating work order:', error);
      this.errorSignal.set(error.message || 'Failed to update work order');
      return null;
    }
  }

  async cancel(id: string, dto: CancelWorkOrderDto): Promise<WorkOrder | null> {
    this.errorSignal.set(null);
    try {
      const response = await firstValueFrom(
        this.http.patch<ApiResponse<WorkOrder>>(`${this.apiUrl}/work-orders/${id}/cancel`, dto),
      );
      if (response.success && response.data) {
        this.upsertCache(response.data);
        return response.data;
      }
      throw new Error(response.error || 'Failed to cancel work order');
    } catch (error: any) {
      console.error('Error cancelling work order:', error);
      this.errorSignal.set(error.message || 'Failed to cancel work order');
      return null;
    }
  }

  /** Drop a WO from the local cache (used by `work-order.deleted`). */
  removeFromCache(id: string): void {
    this.workOrdersSignal.update((list) => list.filter((w) => w.id !== id));
  }

  /** Public so the realtime service can re-sync after a WS event. */
  upsertCache(wo: WorkOrder): void {
    this.workOrdersSignal.update((list) => {
      const idx = list.findIndex((w) => w.id === wo.id);
      if (idx === -1) return [wo, ...list];
      const next = list.slice();
      next[idx] = wo;
      return next;
    });
  }

  /** Synchronous cache read — convenient for the detail screen's
   *  optimistic seed before the API roundtrip lands. */
  getById(id: string): WorkOrder | undefined {
    return this.workOrdersSignal().find((w) => w.id === id);
  }

  clearError(): void {
    this.errorSignal.set(null);
  }
}
